import React from "react";
import { useTranslation } from "react-i18next";
import { PaymentType } from "../../../types/types";
import { getPaymentType } from "../../../utils/deals/dealFormValueUtils";
import { DealFormData, FormValue } from "./dealFormTypes";
import type { PaymentOffsets, PaymentWindows } from "../../../utils/deals/paymentWindows";
import {
  limitDateInputYear,
  readLimitedDateInputValue,
  resolveDateInputMax,
} from "../../../utils/common/dateInput";

export type PayoutItem = {
  original?: unknown;
  index?: number;
  componentType?: string;
  componentLabel?: string;
  value?: number;
  paymentTiming?: string;
  // Percentage support — set by buildPayoutSummary
  isPercentage?: boolean;    // true when component mode === "PERCENT_OF_BASE"
  percentageValue?: number;  // the raw % number (e.g. 56 for "56%")
};

const formatComponentLabel = (value?: string): string =>
  String(value || "")
    .replace(/[_-]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase())
    .trim();

export interface ComponentPaymentsStepProps {
  payoutSummary: PayoutItem[];
  preMatchPayouts: PayoutItem[];
  postMatchPayouts: PayoutItem[];
  preMatchDate: string;
  setPreMatchDate: (d: string) => void;
  postMatchDate: string;
  setPostMatchDate: (d: string) => void;
  formData: DealFormData;
  dateWindows?: PaymentWindows | null;
  deadlineOffsets?: Pick<PaymentOffsets, "prePayment" | "postPayment"> | null;
  dateErrors?: {
    pre: string;
    post: string;
  };
  submitAttempted?: boolean;
}

const PaymentRow: React.FC<{
  item: PayoutItem;
  variant: "teal" | "slate";
  currency?: string;
}> = ({ item, variant, currency }) => {
  const { t } = useTranslation("dealModal");
  const styles =
    variant === "teal"
      ? {
          dot: "bg-teal-500",
          amountBadge: "bg-teal-50 text-teal-700 border-teal-200",
          percentagePill: "bg-teal-100 text-teal-600 border-teal-200",
        }
      : {
          dot: "bg-slate-400",
          amountBadge: "bg-slate-100 text-slate-700 border-slate-200",
          percentagePill: "bg-slate-200 text-slate-500 border-slate-300",
        };

  const resolvedAmount = Number(item.value ?? 0);
  const label =
    item.componentLabel || formatComponentLabel(item.componentType);

  return (
    <div className="flex items-center gap-4 py-3 px-4 hover:bg-slate-50/80 rounded-lg w-full">
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${styles.dot}`} />
        <p className="text-[13px] font-medium text-slate-700 truncate">
          {label}
        </p>
      </div>

      {/* Right: percentage pill (only for PERCENT_OF_BASE) + resolved amount */}
      <div className="flex items-center gap-2 flex-shrink-0">
        {item.isPercentage && item.percentageValue != null && (
          <span
            className={`inline-flex items-center gap-0.5 px-2 py-0.5 rounded-md border text-[11px] font-semibold tabular-nums ${styles.percentagePill}`}
            title={t("percentOfBaseTitle", { value: item.percentageValue })}
          >
            {item.percentageValue}
            <span className="opacity-70">%</span>
            <svg
              className="w-2.5 h-2.5 opacity-50"
              fill="none"
              stroke="currentColor"
              strokeWidth={2.5}
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </span>
        )}

        <span
          className={`px-2.5 py-1 rounded-lg border text-[12px] font-bold tabular-nums ${styles.amountBadge}`}
        >
          {currency && (
            <span className="mr-0.5 font-normal opacity-60">{currency}</span>
          )}
          {resolvedAmount.toLocaleString(undefined, {
            minimumFractionDigits: resolvedAmount % 1 !== 0 ? 2 : 0,
            maximumFractionDigits: 2,
          })}
        </span>
      </div>
    </div>
  );
};

const InlineDatePicker: React.FC<{
  value: string;
  onChange: (v: string) => void;
  min?: string;
  max?: string;
  error?: string;
  accent?: "blue" | "teal";
}> = ({ value, onChange, min, max, error, accent = "blue" }) => {
  const { t } = useTranslation("dealModal");
  const hasError = !!error;

  const accentBorder =
    accent === "teal"
      ? "border-teal-200 hover:border-teal-400 focus:border-teal-500 focus:ring-teal-100"
      : "border-slate-200 hover:border-slate-400 focus:border-blue-500 focus:ring-blue-100";

  const errorBorder =
    "border-red-400 hover:border-red-500 focus:border-red-500 focus:ring-red-100";

  return (
    <div className="flex w-[200px] max-w-full flex-col items-end gap-1">
      <input
        type="date"
        value={limitDateInputYear(value ?? "")}
        min={min}
        max={resolveDateInputMax(max)}
        aria-label={t("paymentDueDate")}
        aria-invalid={hasError ? true : undefined}
        pattern="\d{4}-\d{2}-\d{2}"
        onInput={(e) => readLimitedDateInputValue(e.currentTarget)}
        onChange={(e) => onChange(readLimitedDateInputValue(e.currentTarget))}
        className={[
          "w-full rounded-lg border bg-white px-2 text-[12px] text-slate-800",
          "h-8 shadow-none outline-none transition-all duration-150",
          "focus:ring-2 [color-scheme:light]",
          hasError ? errorBorder : accentBorder,
        ].join(" ")}
      />
      {hasError && (
        <p className="text-[11px] text-red-500 font-medium text-right max-w-[200px] leading-snug">
          {error}
        </p>
      )}
    </div>
  );
};

const WindowHint: React.FC<{
  type: "pre" | "post";
  offsetDays?: number;
  dateWindows?: PaymentWindows | null;
}> = ({ type, offsetDays, dateWindows }) => {
  const { t } = useTranslation("dealModal");
  if (!dateWindows || offsetDays === undefined) return null;
  const isPre = type === "pre";
  const range = isPre
    ? `${dateWindows.preMin} → ${dateWindows.preMax}`
    : `${dateWindows.postMin} → ${dateWindows.postMax}`;
  const hint = isPre
    ? t("upToDaysBeforeEvent", { days: offsetDays })
    : t("upToDaysAfterEvent", { days: offsetDays });
  return (
    <span className="text-[11px] text-slate-400 ml-1 tabular-nums">
      ({hint}: {range})
    </span>
  );
};

const ComponentPaymentsStep: React.FC<ComponentPaymentsStepProps> = ({
  payoutSummary,
  preMatchPayouts,
  postMatchPayouts,
  preMatchDate,
  setPreMatchDate,
  postMatchDate,
  setPostMatchDate,
  formData,
  dateWindows,
  deadlineOffsets,
  dateErrors,
  submitAttempted = false,
}) => {
  const { t } = useTranslation("dealModal");
  const currency   = ((formData?.core as Record<string, FormValue> | undefined)?.currency || "") as string;
  const preOffset  = deadlineOffsets?.prePayment;
  const postOffset = deadlineOffsets?.postPayment;

  const visibleErrors = submitAttempted ? dateErrors : undefined;

  const showPostMatch = getPaymentType(formData) === PaymentType.SPLIT;

  const preTotal = preMatchPayouts.reduce(
    (sum, payout) => sum + Number(payout?.value ?? 0),
    0,
  );
  const postTotal = postMatchPayouts.reduce(
    (sum, payout) => sum + Number(payout?.value ?? 0),
    0,
  );

  return (
    <div className="space-y-4">
      {payoutSummary.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center mb-4">
            <svg className="w-7 h-7 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
            </svg>
          </div>
          <p className="text-[14px] font-semibold text-slate-500">
            {t("noPayoutComponentsConfigured")}
          </p>
          <p className="text-[12px] text-slate-400 mt-1">
            {t("addComponentsInPreviousStep")}
          </p>
        </div>
      ) : (
        <>
          {preMatchPayouts.length > 0 && (
            <div
              className={[
                "border rounded-xl",
                visibleErrors?.pre ? "border-red-300" : "border-teal-200",
              ].join(" ")}
            >
              <div
                className={[
                  "flex items-center justify-between px-4 py-2.5 border-b rounded-t-xl",
                  visibleErrors?.pre ? "bg-red-50/60 border-red-100" : "bg-teal-50/70 border-teal-100",
                ].join(" ")}
              >
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${visibleErrors?.pre ? "bg-red-400" : "bg-teal-500"}`} />
                  <span className={`text-[12px] font-bold ${visibleErrors?.pre ? "text-red-600" : "text-teal-700"}`}>
                    {t("preMatchPayments")}
                  </span>
                  <WindowHint type="pre" offsetDays={preOffset} dateWindows={dateWindows} />
                </div>
                <InlineDatePicker
                  value={preMatchDate}
                  onChange={setPreMatchDate}
                  min={dateWindows?.preMin}
                  max={dateWindows?.preMax}
                  error={visibleErrors?.pre}
                  accent="teal"
                />
              </div>
              <div className="px-1 py-1">
                {preMatchPayouts.map((item, i) => (
                  <PaymentRow key={i} item={item} variant="teal" currency={currency} />
                ))}
              </div>
              <div
                className={[
                  "flex items-center justify-end gap-2 px-4 py-2.5 border-t",
                  visibleErrors?.pre ? "bg-red-50/60 border-red-100" : "bg-teal-50/70 border-teal-100",
                ].join(" ")}
              >
                <span className="text-[11px] font-medium text-teal-400 uppercase tracking-wide">
                  {t("total")}
                </span>
                <span className="text-[13px] font-bold text-teal-700 tabular-nums min-w-[80px] text-right pr-4">
                  {currency && <span className="font-normal opacity-60 mr-0.5 text-[11px]">{currency}</span>}
                  {preTotal.toLocaleString(undefined, {
                    minimumFractionDigits: preTotal % 1 !== 0 ? 2 : 0,
                    maximumFractionDigits: 2,
                  })}
                </span>
              </div>
            </div>
          )}

          {showPostMatch && postMatchPayouts.length > 0 && (
            <div
              className={[
                "border rounded-xl",
                visibleErrors?.post ? "border-red-300" : "border-slate-200",
              ].join(" ")}
            >
              <div
                className={[
                  "flex items-center justify-between px-4 py-2.5 border-b rounded-t-xl",
                  visibleErrors?.post ? "bg-red-50/60 border-red-100" : "bg-slate-50 border-slate-100",
                ].join(" ")}
              >
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${visibleErrors?.post ? "bg-red-400" : "bg-slate-400"}`} />
                  <span className={`text-[12px] font-bold ${visibleErrors?.post ? "text-red-600" : "text-slate-600"}`}>
                    {t("postMatchPayments")}
                  </span>
                  <WindowHint type="post" offsetDays={postOffset} dateWindows={dateWindows} />
                </div>
                <InlineDatePicker
                  value={postMatchDate}
                  onChange={setPostMatchDate}
                  min={dateWindows?.postMin}
                  max={dateWindows?.postMax}
                  error={visibleErrors?.post}
                  accent="blue"
                />
              </div>
              <div className="px-1 py-1">
                {postMatchPayouts.map((item, i) => (
                  <PaymentRow key={i} item={item} variant="slate" currency={currency} />
                ))}
              </div>
              <div
                className={[
                  "flex items-center justify-end gap-2 px-4 py-2.5 border-t",
                  visibleErrors?.post ? "bg-red-50/60 border-red-100" : "bg-slate-50 border-slate-100",
                ].join(" ")}
              >
                <span className="text-[11px] font-medium text-slate-400 uppercase tracking-wide">
                  {t("total")}
                </span>
                <span className="text-[13px] font-bold text-teal-700 tabular-nums min-w-[80px] text-right pr-4">
                  {currency && <span className="font-normal opacity-60 mr-0.5 text-[11px]">{currency}</span>}
                  {postTotal.toLocaleString(undefined, {
                    minimumFractionDigits: postTotal % 1 !== 0 ? 2 : 0,
                    maximumFractionDigits: 2,
                  })}
                </span>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ComponentPaymentsStep;
